import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "../axios";
import { showDate } from "../components/ShowPieces";

function dollars(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return "";
  }
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function contactRow(icon, text, href) {
  if (!text) {
    return null;
  }
  return (
    <p className="cart-contact">
      <i className={`fa-solid ${icon}`} aria-hidden="true"></i>
      {href ? <a href={href}>{text}</a> : <span>{text}</span>}
    </p>
  );
}

function CartScreen() {
  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const [cart, setCart] = useState(null);
  const [profile, setProfile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
      return undefined;
    }
    let cancel = false;
    const headers = { Authorization: `Bearer ${userInfo.token}` };
    axios
      .get("/api/users/checkout/", { headers })
      .then(({ data }) => {
        if (!cancel) {
          setCart(data);
        }
      })
      .catch(() => {
        if (!cancel) {
          setError("The cart could not be opened.");
        }
      });
    axios
      .get("/api/users/profile/", { headers })
      .then(({ data }) => {
        if (!cancel) {
          setProfile(data);
        }
      })
      .catch(() => {
        if (!cancel) {
          setProfile(null);
        }
      });
    axios
      .get("/api/users/countries/", { headers })
      .then(({ data }) => {
        if (!cancel) {
          setCountries(data || []);
        }
      })
      .catch(() => {
        if (!cancel) {
          setCountries([]);
        }
      });
    return () => {
      cancel = true;
    };
  }, [navigate, userInfo]);

  const events = cart?.events || [];
  const person = profile?.profile || {};
  const fullName = `${person.first_name || ""} ${person.last_name || ""}`.trim() || userInfo?.name || "";
  const email = profile?.email || userInfo?.email || "";
  const country = countries.find((item) => String(item.id) === String(person.country));
  const cityLine = [person.city, person.state].filter(Boolean).join(", ");
  const cityZip = [cityLine, person.zip_code].filter(Boolean).join(" ");
  const addressLines = [person.address, person.address2, cityZip, country?.name].filter(Boolean);

  return (
    <div className="beach">
      <section className="site-section">
        <div className="site-wrap">
          <p className="site-kicker">Your order</p>
          <h1>Cart</h1>
          <p className="cart-lead">
            Each day is its own registration. Saturday and Sunday stay separate, and you can add the other day from
            that day's page.
          </p>
          {error ? <p className="text-danger">{error}</p> : null}
          {cart === null && !error ? <p>Loading the cart.</p> : null}
          {cart && events.length === 0 ? (
            <p className="cart-empty">
              Nothing is in the cart yet. <Link to="/show">See the show days.</Link>
            </p>
          ) : null}
          <div className="cart-days">
            {events.map((event) => (
              <article key={event.id} className="cart-day">
                <header className="cart-day-head">
                  <div>
                    <p className="ticket-date">{showDate(event.date)}</p>
                    <h2>{event.title}</h2>
                  </div>
                  <Link
                    className="cart-day-edit"
                    to={`/show/${event.id}?checkout=1`}
                    aria-label={`Edit Ticket, ${event.title}`}
                    title={`Edit Ticket, ${event.title}`}
                  >
                    <svg className="receipt-edit-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <path
                        d="M11.2 2.4l2.4 2.4-7.6 7.6H3.6V10z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                      <path d="M9.6 4l2.4 2.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
                    </svg>
                    <span>Edit Ticket</span>
                  </Link>
                </header>
                <ul>
                  {event.lines.map((line, index) => (
                    <li key={`${event.id}-${index}`}>
                      <span>
                        {line.label}
                        {line.detail ? <span className="ticket-free-note"> {line.detail}</span> : null}
                      </span>
                      <strong>{dollars(line.amount)}</strong>
                    </li>
                  ))}
                </ul>
                <p className="cart-day-total">
                  <span>Today's Total</span>
                  <strong>{dollars(event.total)}</strong>
                </p>
              </article>
            ))}
          </div>
          {events.length > 0 ? (
            <footer className="cart-foot">
              <div className="cart-pay">
                <p className="cart-pay-total">
                  <span>Total</span>
                  <strong>{dollars(cart.balance)}</strong>
                </p>
                <button type="button" className="btn-purchase">
                  Purchase
                </button>
              </div>
              <div className="cart-profile">
                {fullName ? <p className="receipt-name">{fullName}</p> : null}
                {person.company ? <p>{person.company}</p> : null}
                {contactRow("fa-envelope", email, email ? `mailto:${email}` : "")}
                {addressLines.length ? (
                  <p className="cart-contact">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                    <span>
                      {addressLines.map((line, index) => (
                        <span key={`${line}-${index}`}>
                          {index > 0 ? <br /> : null}
                          {line}
                        </span>
                      ))}
                    </span>
                  </p>
                ) : null}
                {contactRow("fa-phone", person.cell_phone ? `Cell ${person.cell_phone}` : "", person.cell_phone ? `tel:${person.cell_phone}` : "")}
                {contactRow("fa-phone", person.home_phone ? `Home ${person.home_phone}` : "", person.home_phone ? `tel:${person.home_phone}` : "")}
              </div>
            </footer>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default CartScreen;
